import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "shared/ui/shadcn/button";
import { clsxMerge } from "shared/lib/helpers";
import { useCallback, useState } from "react";
import { useTimeout } from "shared/lib/hooks";
import { toast } from "shared/ui/shadcn/sonner";
import { TIME_INTERVALS } from "shared/lib/constants/time";

import { LookalikeGuestBlock } from "@/modals/book-meeting-shared/lookalike-guest-block";

const BOOKING_STEPS = [
  "Click the button below to open the scheduling page",
  "Select an available time slot that works for the prospect",
  "Set prospect name and email as an invitee info",
  "Complete the booking form in the popup window and close it",
  "Return here to confirm your booking",
];

const CONFIRM_BUTTON_DELAY_MS = TIME_INTERVALS.ONE_SECOND * 5;

interface InstructionsStepPropsI {
  schedulingLink: string | null | undefined;
  // False when the campaign resolves to no usable scheduling URL — the "Open
  // scheduling link" button would otherwise silently do nothing.
  canOpenSchedulingLink?: boolean;
  isReschedule?: boolean;
  campaignId: string | undefined;
  onOpenSchedulingLink: () => void;
  onVerifyBooking: () => void;
}

export const InstructionsStep = ({
  schedulingLink,
  canOpenSchedulingLink = true,
  isReschedule,
  campaignId,
  onOpenSchedulingLink,
  onVerifyBooking,
}: InstructionsStepPropsI) => {
  const [showConfirmSection, setShowConfirmSection] = useState(false);

  // Show confirm button after delay to give SDR time to complete booking
  useTimeout(() => setShowConfirmSection(true), CONFIRM_BUTTON_DELAY_MS);

  const handleCopyLink = useCallback(() => {
    if (schedulingLink) {
      navigator.clipboard.writeText(schedulingLink);
      toast.success("Link copied to clipboard");
    }
  }, [schedulingLink]);

  return (
    <div className="space-y-5">
      {/* Step cards */}
      <div className="space-y-2">
        {BOOKING_STEPS.map((stepText, i) => (
          <div
            key={i}
            className="flex items-start gap-3 rounded-lg border border-base-300 bg-base-100 p-3 transition-colors hover:bg-base-200/50"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black text-xs font-medium text-white">
              {i + 1}
            </span>
            <p className="pt-0.5 text-base-content/80 typography-body-4">
              {stepText}
            </p>
          </div>
        ))}
      </div>

      <section className="flex flex-col gap-2">
        {/* Primary CTA */}
        <Button
          className="w-full"
          variant={showConfirmSection ? "secondary" : "default"}
          onClick={onOpenSchedulingLink}
          disabled={!canOpenSchedulingLink}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          Open scheduling link
        </Button>

        {!canOpenSchedulingLink && (
          <p
            data-testid="no-scheduling-link-notice"
            className="text-base-content/60 typography-body-5"
          >
            No scheduling link is configured for this campaign. Set a default
            scheduling link (Account Executives → Set Default Scheduling Link)
            to enable it.
          </p>
        )}

        {/* Lookalike guest (copy / verify). Renders only for lookalike
            campaigns; shared with the in-call dialer booking modal. */}
        <LookalikeGuestBlock
          campaignId={campaignId}
          schedulingLink={schedulingLink}
          className="mt-2"
        />

        {/* Confirmation section - delayed reveal */}
        <div
          className={clsxMerge(
            "mt-2 overflow-hidden border-t border-base-200 pt-2 transition-all duration-500 ease-out",
            showConfirmSection
              ? "max-h-40 opacity-100"
              : "pointer-events-none max-h-0 opacity-0"
          )}
        >
          <div className={clsxMerge(showConfirmSection && "pb-4")}>
            <p className="mb-3 text-center text-sm text-base-content/60">
              {isReschedule
                ? "Finished rescheduling on the external page?"
                : "Finished booking on the external page?"}
            </p>
            <Button size="lg" className="w-full" onClick={onVerifyBooking}>
              <CheckCircleIcon className="h-4 w-4" />
              {isReschedule
                ? "I've rescheduled the meeting"
                : "I've booked the meeting"}
            </Button>
          </div>
        </div>

        {/* Subtle copy link */}
        <Button
          variant="ghost"
          className="mx-auto w-full text-base-content/50 hover:text-base-content/70"
          onClick={handleCopyLink}
        >
          <ClipboardDocumentIcon className="h-4 w-4" />
          Copy link instead
        </Button>
      </section>
    </div>
  );
};
